import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AgentPortalError from 'errors/AgentPortalError';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { CANCEL_TRAVEL_PASS_PRODUCT } from 'store/mutations';
import moment from 'moment';
import { CurrencyInput } from 'components/ui/Inputs';
import SubscriptionDetails from '../../../BookingDetails/Extras/ExtrasSubscriptions/SubscriptionDetails';
import { useForm } from 'react-hook-form';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ActionButton, ConfirmButton } from 'components/ui/Button';
import { COMMERCIAL_GESTURE } from 'constants/subscriptionCancellationReasons';
import { getRefundMethodsURL } from 'utils/urls';
import {
  getCancellationFees,
  getDateObjects,
  cancellationMessage
} from '../../../BookingDetails/Extras/ExtrasSubscriptions/ExtrasSubscriptions.utils';
import {
  CancellationFeeWrapper,
  CancellationFeeInput,
  CancellationFeeMessage,
  FlexWrapper,
  StyledNoRevert,
  StyledInfos
} from './Subscription.styles';
import { StyledDisplayFeesBreakdown } from '../../../BookingDetails/Extras/Extras.styles';
import {
  StyledButtonGroup,
  StyledFormFooter,
  StyledInputWrapper
} from '../../../BookingDetails/Extras/ExtrasHotel/ManageHotel/ManageHotel.styles';
import { subtract, sum } from 'utils/calculator';

const DisplayFeesBreakdown = ({ daysAfterSubscriptionPaid, daysAfterSubscriptionStartDate, monthsUsedOfSubscription }) => (
  <StyledDisplayFeesBreakdown>
    {daysAfterSubscriptionPaid <= 14 && <FormattedMessage id="subscription.noCancellationFee" />}
    {daysAfterSubscriptionPaid > 14 && daysAfterSubscriptionStartDate <= 14 && (
      <FormattedMessage id="subscription.noCancellationFeeAfterDateStart" />
    )}
    {daysAfterSubscriptionPaid > 14 && daysAfterSubscriptionStartDate > 14 && (
      <FormattedMessage id="subscription.cancellationFeeMonths" values={{ monthsAfterStart: monthsUsedOfSubscription }} />
    )}
  </StyledDisplayFeesBreakdown>
);

const ProrataCancellation = ({
  itemRef,
  passenger,
  startDate,
  endDate,
  name,
  currency,
  price,
  bookingReference,
  confirmedTimestamp,
  bookingWithin14DaysCoolingOffPeriod,
  onClose,
  reason,
  membershipNumber,
  travelPassReference,
  hasPastBookings
}) => {
  const [preferentialRate, setPreferentialRate] = useState(0);
  const cancellingForCommercialGesture = reason === COMMERCIAL_GESTURE;
  const { push } = useHistory();
  const { handleSubmit, register, setValue } = useForm();

  const [cancelTravelPassProduct, { loading: cancelLoading, error: cancelError }] = useMutation(CANCEL_TRAVEL_PASS_PRODUCT, {
    onCompleted: ({ cancelTravelPassProduct: { paymentsRefundBalance } }) => {
      if (paymentsRefundBalance < 0) {
        push(getRefundMethodsURL(bookingReference, currency, membershipNumber, false, true));
      }
    },
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const dateObjects = getDateObjects(confirmedTimestamp, endDate, startDate);
  const cancellationFees = cancellingForCommercialGesture ? 0 : getCancellationFees(price, confirmedTimestamp, endDate, startDate);

  const handleOnSubmit = async () => {
    await cancelTravelPassProduct({
      variables: {
        bookingReference,
        itemRef,
        fee: sum([cancellationFees, preferentialRate]),
        travelPassReference,
        cancellationReason: reason
      }
    });
  };

  if (cancelError) {
    throw new AgentPortalError(cancelError);
  }

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} data-cy="subscription-form" data-testid="subscription-form-prorata">
      <SubscriptionDetails
        firstName={passenger?.firstName}
        lastName={passenger?.lastName}
        startDate={moment(startDate).format('YYYY-MM-DD')}
        endDate={moment(endDate).format('YYYY-MM-DD')}
        name={name}
        currency={currency}
        price={price}
        bookingWithin14DaysCoolingOffPeriod={bookingWithin14DaysCoolingOffPeriod}
      />

      <StyledInfos>
        <div>
          <FormattedMessage id="thalyspass.purchase" />:{' '}
          {bookingWithin14DaysCoolingOffPeriod ? (
            <FormattedMessage id="subscription.withinCoolingOff" />
          ) : (
            <FormattedMessage id="subscription.outsideCoolingOff" />
          )}
        </div>
        <div>
          <FormattedMessage id="subscriptions.cancellationReasonTickets" />: {reason}
        </div>
        <div>
          <FormattedMessage id="subscriptions.ticketsTravelled" />:{' '}
          {hasPastBookings ? <FormattedMessage id="interaction.yes" /> : <FormattedMessage id="interaction.no" />}
        </div>
      </StyledInfos>

      <CancellationFeeWrapper>
        <CancellationFeeInput>
          <StyledInputWrapper>
            <strong>
              <FormattedMessage id="bookingSummary.cancellationFees" />
            </strong>

            <FlexWrapper data-testid="currency-cancellationFees">
              <FormattedCurrency currency={currency} amount={cancellationFees} dataCy="currency-cancellationFees" />
              {!cancellingForCommercialGesture && (
                <DisplayFeesBreakdown
                  daysAfterSubscriptionPaid={dateObjects.daysDiffBetweenTodayAndBookingCreation}
                  daysAfterSubscriptionStartDate={dateObjects.daysDiffBetweenTodayAndStartDate}
                  monthsUsedOfSubscription={Math.abs(dateObjects.monthsDiffBetweenTodayAndStartDate)}
                />
              )}
            </FlexWrapper>
          </StyledInputWrapper>
        </CancellationFeeInput>

        <CancellationFeeInput>
          <StyledInputWrapper>
            <strong>
              <FormattedMessage id="extras.insurance.modal.total" />
            </strong>
            <FlexWrapper data-testid="currency-price-cancellation-fees">
              <FormattedCurrency currency={currency} amount={price - cancellationFees} dataCy="currency-price-cancellation-fees" />
              {!cancellingForCommercialGesture && (
                <CancellationFeeMessage>
                  <FormattedMessage
                    dataCy="currency-price-cancellation-fees-message"
                    id="subscription.cancellationForNumberOfMonths"
                    values={{
                      month: cancellationMessage(dateObjects)
                    }}
                  />
                </CancellationFeeMessage>
              )}
            </FlexWrapper>
          </StyledInputWrapper>
        </CancellationFeeInput>

        {hasPastBookings && (
          <>
            <CancellationFeeInput>
              <StyledInputWrapper>
                <strong>
                  <FormattedMessage id="subscriptions.preferentialRates" />:
                </strong>

                <FlexWrapper data-testid="currency-price-preferential-rates-fees">
                  <CurrencyInput
                    currency={currency}
                    dataCy="preferentialRates"
                    dataTestId="preferentialRates"
                    value={preferentialRate}
                    min={0}
                    max={subtract(price, cancellationFees)}
                    {...register('preferentialRates', {
                      required: true,
                      onChange: (event) => {
                        const newValue = event.target.value;

                        setValue('preferentialRates', newValue);
                        setPreferentialRate(newValue);
                      }
                    })}
                    step={0.01}
                  />
                </FlexWrapper>
              </StyledInputWrapper>
            </CancellationFeeInput>

            <CancellationFeeInput>
              <StyledInputWrapper>
                <strong>
                  <FormattedMessage id="extras.refundAmountMessage" />:
                </strong>
                <FlexWrapper data-testid="currency-price-preferential-rates">
                  <FormattedCurrency
                    currency={currency}
                    amount={price - cancellationFees - preferentialRate}
                    dataCy="currency-price-preferential-rates"
                  />
                </FlexWrapper>
              </StyledInputWrapper>
            </CancellationFeeInput>
          </>
        )}
      </CancellationFeeWrapper>
      <StyledNoRevert>
        <FormattedMessage id="subscription.noRevert" />
      </StyledNoRevert>
      <StyledFormFooter>
        <StyledButtonGroup>
          <ConfirmButton
            type="submit"
            data-testid="submit"
            data-cy="submit"
            disabled={cancelLoading}
            loading={cancelLoading}
            variant="primary"
          >
            <FormattedMessage id="extras.hotel.modal.confirm" />
          </ConfirmButton>

          <ActionButton type="button" data-testid="close" onClick={onClose} disabled={cancelLoading}>
            <FormattedMessage id="extras.hotel.modal.close" />
          </ActionButton>
        </StyledButtonGroup>
      </StyledFormFooter>
    </form>
  );
};

ProrataCancellation.propTypes = {
  itemRef: PropTypes.string,
  passenger: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string
  }),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  name: PropTypes.string,
  currency: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingReference: PropTypes.string,
  confirmedTimestamp: PropTypes.string,
  onClose: PropTypes.func,
  reason: PropTypes.string,
  bookingWithin14DaysCoolingOffPeriod: PropTypes.bool,
  membershipNumber: PropTypes.string,
  travelPassReference: PropTypes.string,
  hasPastBookings: PropTypes.bool
};

DisplayFeesBreakdown.propTypes = {
  daysAfterSubscriptionPaid: PropTypes.number,
  daysAfterSubscriptionStartDate: PropTypes.number,
  monthsUsedOfSubscription: PropTypes.number
};

export default ProrataCancellation;
